import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, DocumentArrowUpIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { VendorDocument } from '../../models/MasterAdmin/doctorClinic';
import { adminUploadOrReplaceVendorDocument } from '../../services/vendorDocumentsService';

const DEFAULT_DOCUMENT_TYPES = [
  { value: 'ESTABLISHMENT_PROOF', label: 'Establishment Proof' },
  { value: 'DEGREE_CERTIFICATE', label: 'Degree Certificate' },
  { value: 'IDENTITY_PROOF', label: 'Identity Proof' },
  { value: 'REGISTRATION_CERTIFICATE', label: 'Registration Certificate' },
  { value: 'OTHER', label: 'Other Document' },
];

interface AdminUploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  vendorId: string;
  vendorType?: number;
  existingDocuments?: VendorDocument[];
  initialDocument?: VendorDocument | null;
  onSuccess: (updatedDoc: any) => void;
}

const AdminUploadDocumentModal: React.FC<AdminUploadDocumentModalProps> = ({
  isOpen,
  onClose,
  vendorId,
  vendorType = 2,
  existingDocuments = [],
  initialDocument,
  onSuccess,
}) => {
  const [documentType, setDocumentType] = useState('ESTABLISHMENT_PROOF');
  const [documentName, setDocumentName] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [issuedBy, setIssuedBy] = useState('');
  const [issuedDate, setIssuedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [isLifetime, setIsLifetime] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Find if current selected type matches an existing document
  const matchedExistingDoc = useMemo(() => {
    if (initialDocument) return initialDocument;
    if (!documentType || documentType === 'OTHER') return null;
    return existingDocuments.find((d) => d.documentType === documentType) || null;
  }, [initialDocument, documentType, existingDocuments]);

  const isReplace = Boolean(matchedExistingDoc);

  useEffect(() => {
    if (initialDocument) {
      setDocumentType(initialDocument.documentType || 'ESTABLISHMENT_PROOF');
      setDocumentName(initialDocument.documentName || '');
      setDocumentNumber(initialDocument.documentNumber || '');
      setIssuedBy(initialDocument.issuedBy || '');
      setIssuedDate(initialDocument.issuedDate || '');
      setExpiryDate(initialDocument.expiryDate || '');
      setIsLifetime(Boolean(initialDocument.isLifetime));
    } else {
      // Pick first un-uploaded type if available, else fallback to ESTABLISHMENT_PROOF
      const uploadedTypes = new Set(existingDocuments.map((d) => d.documentType));
      const firstUnuploaded = DEFAULT_DOCUMENT_TYPES.find(
        (t) => t.value !== 'OTHER' && !uploadedTypes.has(t.value)
      );
      const defaultType = firstUnuploaded ? firstUnuploaded.value : 'ESTABLISHMENT_PROOF';

      setDocumentType(defaultType);
      const matched = DEFAULT_DOCUMENT_TYPES.find((t) => t.value === defaultType);
      setDocumentName(matched ? matched.label : '');
      setDocumentNumber('');
      setIssuedBy('');
      setIssuedDate('');
      setExpiryDate('');
      setIsLifetime(false);
    }
    setSelectedFile(null);
    setError(null);
  }, [initialDocument, isOpen, existingDocuments]);

  const handleDocumentTypeChange = (typeVal: string) => {
    setDocumentType(typeVal);
    const existing = existingDocuments.find((d) => d.documentType === typeVal && typeVal !== 'OTHER');
    if (existing) {
      setDocumentName(existing.documentName || typeVal);
      setDocumentNumber(existing.documentNumber || '');
      setIssuedBy(existing.issuedBy || '');
      setIssuedDate(existing.issuedDate || '');
      setExpiryDate(existing.expiryDate || '');
      setIsLifetime(Boolean(existing.isLifetime));
    } else {
      const matched = DEFAULT_DOCUMENT_TYPES.find((t) => t.value === typeVal);
      if (matched) {
        setDocumentName(matched.value === 'OTHER' ? '' : matched.label);
      }
      setDocumentNumber('');
      setIssuedBy('');
      setIssuedDate('');
      setExpiryDate('');
      setIsLifetime(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile && !isReplace) {
      setError('Please select a file to upload.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append('vendorId', vendorId);
      formData.append('vendorType', String(vendorType));
      formData.append('documentType', documentType);
      formData.append('documentName', documentName.trim() || documentType);
      if (documentNumber.trim()) formData.append('documentNumber', documentNumber.trim());
      if (issuedBy.trim()) formData.append('issuedBy', issuedBy.trim());
      if (issuedDate) formData.append('issuedDate', issuedDate);
      if (isLifetime) {
        formData.append('isLifetime', 'true');
      } else if (expiryDate) {
        formData.append('expiryDate', expiryDate);
      }

      const targetDocId = matchedExistingDoc?.documentId || initialDocument?.documentId;
      if (targetDocId) {
        formData.append('documentId', targetDocId);
      }

      if (selectedFile) {
        formData.append('documentFile', selectedFile);
      }

      const updatedDoc = await adminUploadOrReplaceVendorDocument(formData);
      if (!updatedDoc) {
        throw new Error('Failed to upload/replace document.');
      }

      onSuccess(updatedDoc);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Error uploading document');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        {/* Header - Fixed Top */}
        <div className="flex items-center justify-between border-b dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-800 shrink-0">
          <div className="flex items-center gap-2">
            <DocumentArrowUpIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {isReplace ? 'Replace Document' : 'Upload New Document'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden min-h-0">
          {/* Scrollable Form Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 text-sm">
            {error && (
              <div className="p-3 text-sm rounded-lg bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                {error}
              </div>
            )}

            {isReplace && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 text-xs">
                <InformationCircleIcon className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
                <div>
                  <p className="font-semibold">Document type already exists</p>
                  <p className="mt-0.5">
                    Uploading a file for <span className="underline font-medium">{documentType}</span> will update the existing record in MySQL and replace the file in Firebase.
                  </p>
                </div>
              </div>
            )}

            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document Type <span className="text-red-500">*</span>
              </label>
              <select
                value={documentType}
                onChange={(e) => handleDocumentTypeChange(e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                {DEFAULT_DOCUMENT_TYPES.map((t) => {
                  const isAlreadyUploaded =
                    t.value !== 'OTHER' && existingDocuments.some((d) => d.documentType === t.value);
                  return (
                    <option key={t.value} value={t.value}>
                      {t.label} {isAlreadyUploaded ? '(Uploaded - Will Replace)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder="e.g. Degree Certificate, PAN Card, etc."
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document Number
              </label>
              <input
                type="text"
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                placeholder="e.g. DRK-198701 or License No."
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                Issued By
              </label>
              <input
                type="text"
                value={issuedBy}
                onChange={(e) => setIssuedBy(e.target.value)}
                placeholder="e.g. Maharashtra Medical Council"
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Issued Date
                </label>
                <input
                  type="date"
                  value={issuedDate}
                  onChange={(e) => setIssuedDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expiry Date
                </label>
                <input
                  type="date"
                  disabled={isLifetime}
                  value={isLifetime ? '' : expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isLifetimeCheck"
                checked={isLifetime}
                onChange={(e) => setIsLifetime(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
              />
              <label htmlFor="isLifetimeCheck" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Lifetime validity / No expiry date
              </label>
            </div>

            <div className="pt-2">
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Document File {!isReplace && <span className="text-red-500">*</span>}
              </label>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (f && f.size > 5 * 1024 * 1024) {
                    setError(`Selected file (${(f.size / (1024 * 1024)).toFixed(2)} MB) exceeds the 5 MB size limit. Please select a smaller file.`);
                    e.target.value = '';
                    setSelectedFile(null);
                  } else {
                    setError(null);
                    setSelectedFile(f);
                  }
                }}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-300"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Supported Formats: PDF, PNG, JPG, WEBP, HEIC (Max size: 5 MB)
              </p>
              {isReplace && !selectedFile && (
                <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">
                  Leave file empty to update document metadata without replacing the existing file.
                </p>
              )}
            </div>
          </div>

          {/* Footer - Fixed Bottom */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2 font-medium shadow-sm"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Saving...
                </>
              ) : isReplace ? (
                'Replace & Save'
              ) : (
                'Upload Document'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminUploadDocumentModal;
